import Entity from "../../core/entity/Entity";
import { clamp } from "../../core/util/MathUtil";
import { rUniform } from "../../core/util/Random";
import { LevelEntity } from "../LevelEntity";

// Explosion type definitions
export type ExplosionType = "bomb" | "bigBomb" | "missile" | "bulletImpact";

// Sound parameters for different explosion types
const EXPLOSION_SETTINGS = {
  bomb: {
    baseFrequency: 80,
    duration: 0.8,
    volume: 0.4,
    noiseGain: 0.6,
    filterFreq: 400,
    subBassFreq: 40,
    subBassGain: 0.3,
  },
  bigBomb: {
    baseFrequency: 60,
    duration: 1.2,
    volume: 0.5,
    noiseGain: 0.8,
    filterFreq: 300,
    subBassFreq: 30,
    subBassGain: 0.4,
  },
  missile: {
    baseFrequency: 120,
    duration: 0.6,
    volume: 0.35,
    noiseGain: 0.5,
    filterFreq: 600,
    subBassFreq: 60,
    subBassGain: 0.2,
  },
  bulletImpact: {
    baseFrequency: 300,
    duration: 0.2,
    volume: 0.1,
    noiseGain: 0.2,
    filterFreq: 1000,
    subBassFreq: 0, // No sub-bass for tiny impacts
    subBassGain: 0,
  },
};

export default class ExplosionSoundGenerator
  extends LevelEntity
  implements Entity
{
  private audioContext: AudioContext;

  constructor() {
    super();
    this.tags = ["explosionSoundGenerator"];
    this.id = "explosionSoundGenerator";

    // We'll get the audio context from the game when added
    this.audioContext = null as any;
  }

  onAdd() {
    if (!this.game?.audio) {
      console.warn("No audio context available in game");
      return;
    }

    this.audioContext = this.game.audio;
  }

  /**
   * Handle explosion events
   */
  onExplosion(eventData: {
    explosionType: ExplosionType;
    position: { x: number; y: number };
    radius?: number;
    lifetime?: number;
  }) {
    if (!this.audioContext || !this.game?.masterGain) {
      return;
    }

    this.synthesizeExplosionSound(eventData.explosionType, eventData.radius);
  }

  private synthesizeExplosionSound(
    explosionType: ExplosionType,
    radius?: number,
  ) {
    const settings = EXPLOSION_SETTINGS[explosionType];
    const now = this.audioContext.currentTime;

    // Scale volume based on radius if provided
    const radiusScale = radius ? clamp(radius / 30, 0, 2.0) : 1.0;
    const scaledVolume = settings.volume * radiusScale;

    // Create main explosion components
    this.createExplosionBoom(now, settings, scaledVolume);
    this.createExplosionRumble(now, settings, scaledVolume);
    if (settings.subBassFreq > 0) {
      this.createSubBassImpact(now, settings, scaledVolume);
    }
  }

  private createExplosionBoom(
    now: number,
    settings: any,
    scaledVolume: number,
  ) {
    // Main explosion noise burst
    const bufferSize = this.audioContext.sampleRate * settings.duration;
    const buffer = this.audioContext.createBuffer(
      1,
      bufferSize,
      this.audioContext.sampleRate,
    );
    const data = buffer.getChannelData(0);

    // Generate explosion noise with complex envelope
    for (let i = 0; i < bufferSize; i++) {
      const progress = i / bufferSize;

      // Complex envelope: sharp attack, sustained body, gradual decay
      let envelope;
      if (progress < 0.05) {
        // Sharp attack
        envelope = progress / 0.05;
      } else if (progress < 0.3) {
        // Sustained explosion body
        envelope = 1.0 - (progress - 0.05) * 0.2;
      } else {
        // Gradual decay
        envelope = 0.95 * Math.pow(1 - (progress - 0.3) / 0.7, 1.5);
      }

      data[i] = rUniform(-1, 1) * envelope;
    }

    // Create buffer source
    const noiseSource = this.audioContext.createBufferSource();
    noiseSource.buffer = buffer;

    // Low-pass filter for explosion body
    const explosionFilter = this.audioContext.createBiquadFilter();
    explosionFilter.type = "lowpass";
    explosionFilter.frequency.setValueAtTime(settings.filterFreq * 2, now);
    explosionFilter.frequency.exponentialRampToValueAtTime(
      settings.filterFreq * 0.3,
      now + settings.duration,
    );
    explosionFilter.Q.setValueAtTime(1, now);

    // Gain for volume control
    const noiseGain = this.audioContext.createGain();
    noiseGain.gain.setValueAtTime(scaledVolume * settings.noiseGain, now);

    // Connect and play
    noiseSource.connect(explosionFilter);
    explosionFilter.connect(noiseGain);
    noiseGain.connect(this.game!.masterGain);

    noiseSource.start(now);
    noiseSource.stop(now + settings.duration);

    // Cleanup
    noiseSource.addEventListener("ended", () => {
      noiseSource.disconnect();
      explosionFilter.disconnect();
      noiseGain.disconnect();
    });
  }

  private createExplosionRumble(
    now: number,
    settings: any,
    scaledVolume: number,
  ) {
    // Low-frequency rumble tone
    const oscillator = this.audioContext.createOscillator();
    oscillator.type = "sawtooth";

    // Filter for rumble character
    const rumbleFilter = this.audioContext.createBiquadFilter();
    rumbleFilter.type = "lowpass";
    rumbleFilter.frequency.setValueAtTime(settings.filterFreq, now);
    rumbleFilter.frequency.exponentialRampToValueAtTime(
      80,
      now + settings.duration,
    );
    rumbleFilter.Q.setValueAtTime(2, now);

    // Gain envelope
    const gainNode = this.audioContext.createGain();
    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(scaledVolume * 0.5, now + 0.02); // Quick attack
    gainNode.gain.setValueAtTime(scaledVolume * 0.5, now + 0.1); // Brief sustain
    gainNode.gain.exponentialRampToValueAtTime(
      0.01,
      now + settings.duration * 0.8,
    );

    // Frequency modulation for unstable rumble
    const startFreq = settings.baseFrequency;
    oscillator.frequency.setValueAtTime(startFreq, now);

    // Add some frequency wobble
    const wobbleRate = 8; // Hz
    const wobbleAmount = startFreq * 0.1;
    for (let i = 0; i < 10; i++) {
      const time = now + i * 0.1;
      const wobble = Math.sin(i * wobbleRate) * wobbleAmount;
      oscillator.frequency.setValueAtTime(startFreq + wobble, time);
    }

    // Connect audio graph
    oscillator.connect(rumbleFilter);
    rumbleFilter.connect(gainNode);
    gainNode.connect(this.game!.masterGain);

    // Start and stop
    oscillator.start(now);
    oscillator.stop(now + settings.duration);

    // Cleanup
    oscillator.addEventListener("ended", () => {
      oscillator.disconnect();
      rumbleFilter.disconnect();
      gainNode.disconnect();
    });
  }

  private createSubBassImpact(
    now: number,
    settings: any,
    scaledVolume: number,
  ) {
    // Very low frequency impact for large explosions
    const oscillator = this.audioContext.createOscillator();
    oscillator.type = "sine"; // Pure tone for sub-bass

    // Gain envelope for punch
    const gainNode = this.audioContext.createGain();
    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(
      scaledVolume * settings.subBassGain,
      now + 0.01,
    ); // Very fast attack
    gainNode.gain.exponentialRampToValueAtTime(
      0.01,
      now + settings.duration * 0.5,
    ); // Quick decay

    // Sub-bass frequency
    oscillator.frequency.setValueAtTime(settings.subBassFreq, now);
    oscillator.frequency.exponentialRampToValueAtTime(
      settings.subBassFreq * 0.5,
      now + settings.duration * 0.3,
    );

    // Connect directly to master (no filtering needed for sub-bass)
    oscillator.connect(gainNode);
    gainNode.connect(this.game!.masterGain);

    // Start and stop
    oscillator.start(now);
    oscillator.stop(now + settings.duration * 0.5);

    // Cleanup
    oscillator.addEventListener("ended", () => {
      oscillator.disconnect();
      gainNode.disconnect();
    });
  }
}
